<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { link, push } from 'svelte-spa-router';
  import { api } from '../../framework/js/api.js';
  import { toast } from '../../framework/js/toast.js';
  import { spinner } from '../../framework/js/spinner.js';
  import ConfirmDialog from '../ConfirmDialog.svelte';

  export let assessmentId: string;

  let detail: any = null;
  let loading = true;
  let statusPollId: ReturnType<typeof setInterval> | null = null;
  let activeTab: 'content' | 'answers' = 'content';
  let showDeleteConfirm = false;
  let deleting = false;

  function assessmentDisplayName(d: any): string {
    const line = (d?.the_decision || '').trim().split(/\r?\n/)[0] || '';
    const shortened = line.length > 100 ? `${line.slice(0, 100)}…` : line;
    return shortened || 'this assessment';
  }

  const ANSWER_FIELDS: { key: string; label: string; prompt: string }[] = [
    { key: 'the_decision',  label: 'The decision',   prompt: 'What are you trying to decide in the next 3–6 months?' },
    { key: 'the_assumption',label: 'The assumption', prompt: 'What do you believe to be true — but have not yet proven with data?' },
    { key: 'the_stakes',    label: 'The stakes',     prompt: 'What goes wrong if you get this one wrong?' },
    { key: 'the_customer',  label: 'The customer',   prompt: 'Who exactly are you making this decision for?' },
    { key: 'what_you_know', label: 'What you know',  prompt: 'What confirmed facts or data points do you already have in hand?' },
    { key: 'time_budget',   label: 'Time & budget',  prompt: 'How quickly do you need to move, and what is realistic to spend?' },
  ];

  function stopStatusPoll() {
    if (statusPollId) {
      clearInterval(statusPollId);
      statusPollId = null;
    }
  }

  async function load() {
    stopStatusPoll();
    loading = true;
    try {
      const res: any = await api.getAssessment(assessmentId);
      if (res?.success && res.data) {
        detail = res.data;
        if (['queued', 'processing'].includes(res.data.generation_status)) {
          statusPollId = setInterval(() => {
            void loadQuiet();
          }, 3000);
        }
      } else {
        detail = null;
        toast.showNotification(false, res?.message || 'Failed to load assessment');
      }
    } finally {
      loading = false;
    }
  }

  async function loadQuiet() {
    try {
      const res: any = await api.getAssessment(assessmentId);
      if (res?.success && res.data) {
        detail = res.data;
        if (!['queued', 'processing'].includes(res.data.generation_status)) {
          stopStatusPoll();
        }
      }
    } catch {
      stopStatusPoll();
    }
  }

  function resumeAssessment() {
    push(`/assessments/${assessmentId}/edit`);
  }

  async function cloneAssessment() {
    if (!detail) return;
    spinner.show('Cloning assessment…');
    try {
      const firstLine = (detail.the_decision || '').toString().split(/\r?\n/)[0] || '';
      const sourceName = (detail.name || detail.title || firstLine || 'Assessment').toString().trim();
      const baseName = sourceName.length > 80 ? sourceName.slice(0, 80) : sourceName;
      const clonedName = `${baseName} (copy)`;

      const createRes: any = await api.createAssessment({
        name: clonedName,
        the_decision: detail.the_decision || '',
        the_assumption: detail.the_assumption || '',
        the_stakes: detail.the_stakes || '',
        the_customer: detail.the_customer || '',
        what_you_know: detail.what_you_know || '',
        time_budget: detail.time_budget || '',
      });

      if (!createRes?.success) {
        toast.showNotification(false, createRes?.message || 'Could not clone assessment');
        return;
      }

      const newUuid = createRes.data?.assessment?.uuid;
      if (!newUuid) {
        toast.showNotification(false, 'Unexpected response while cloning.');
        return;
      }

      try {
        localStorage.removeItem(`assessmentDraft_edit_${newUuid}`);
        localStorage.removeItem(`assessment_lastStep_${newUuid}`);
      } catch (_) {}

      toast.showNotification(true, 'Assessment cloned.');
      push(`/assessments/${newUuid}/edit`);
    } finally {
      spinner.hide();
    }
  }

  async function startBrief() {
    if (detail?.generation_status !== 'completed') return;
    spinner.show('Starting brief…');
    try {
      const assessmentName = (detail?.name || detail?.title || '').trim();
      const body: any = await api.startBriefFromAssessment(
        assessmentId,
        assessmentName || null,
      );
      if (!body.success) {
        toast.showNotification(false, body.message || 'Could not start brief');
        return;
      }
      const path = body.data?.redirect_path;
      if (path) {
        push(path);
      }
    } finally {
      spinner.hide();
    }
  }

  async function confirmDeleteAssessment() {
    deleting = true;
    spinner.show('Deleting assessment…');
    try {
      const body: any = await (api as any).deleteAssessment(assessmentId);
      if (!body?.success) {
        toast.showNotification(false, body?.message || 'Could not delete assessment');
        return;
      }
      toast.showNotification(true, 'Assessment deleted.');
      push('/assessments');
    } finally {
      deleting = false;
      spinner.hide();
    }
  }

  async function downloadPdf() {
    const r = await api.downloadAssessmentPdf(assessmentId);
    if (!r?.success) {
      toast.showNotification(false, 'Could not download PDF');
    }
  }

  function formatDate(s: string) {
    if (!s) return '';
    try {
      return new Date(s).toLocaleDateString(navigator.language, { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return s;
    }
  }

  $: result = detail?.result;
  $: canResume =
    detail && !['queued', 'processing', 'completed'].includes(detail.generation_status);
  $: canClone = detail?.generation_status === 'completed';
  $: canStartBrief = detail?.generation_status === 'completed';
  $: deleteConfirmMessage = detail
    ? `Are you sure you want to delete "${assessmentDisplayName(detail)}"? This action cannot be undone.`
    : '';
  $: answersCount = detail
    ? ANSWER_FIELDS.filter(f => (detail[f.key] || '').trim().length > 0).length
    : 0;
  $: statusBadge = (() => {
    if (!detail) return null;
    const s = detail.generation_status;
    if (s === 'queued' || s === 'processing') return { tone: 'info',   label: 'Generating…' };
    if (s === 'failed')    return { tone: 'danger',  label: 'Needs rework' };
    if (s === 'completed') return { tone: 'success', label: 'Ready' };
    return { tone: 'neutral', label: 'Draft' };
  })();

  onMount(load);
  onDestroy(stopStatusPoll);
</script>

{#if loading}
  <div class="loading"><i class="spinner loading icon"></i> Loading…</div>
{:else if !detail}
  <p>Assessment not found.</p>
{:else}
  <div class="view-page">
    <div class="top-bar">
      <a use:link href="/assessments" class="back-link">
        <i class="angle left icon" aria-hidden="true"></i> Back to assessments
      </a>
      <div class="top-actions">
        {#if detail.generation_status === 'completed'}
          <button type="button" class="btn-secondary" on:click={downloadPdf}>
            <i class="download icon" aria-hidden="true"></i> Download PDF
          </button>
        {/if}
        {#if canResume}
          <button type="button" class="btn-accent" on:click={resumeAssessment}>
            <i class="play icon" aria-hidden="true"></i>
            Resume
          </button>
        {/if}
        {#if canClone}
          <button type="button" class="btn-accent" on:click={cloneAssessment}>
            <i class="copy icon" aria-hidden="true"></i>
            Clone
          </button>
        {/if}
        <button
          type="button"
          class="btn-primary"
          on:click={startBrief}
          disabled={!canStartBrief}
          title={canStartBrief
            ? ''
            : 'Finish Illuminator from Resume (last step: Generate Assessment), then you can start a brief.'}
        >
          Start brief from this assessment
        </button>
        <button
          type="button"
          class="btn-danger-outline"
          disabled={deleting}
          on:click={() => (showDeleteConfirm = true)}
        >
          <i class="trash icon" aria-hidden="true"></i>
          Delete
        </button>
      </div>
    </div>

    <div class="agent-banner">
      <span class="agent-badge">ILLUMINATOR</span>
      <div class="agent-banner-right">
        <div class="agent-banner-title">DECISION ASSESSMENT</div>
        {#if detail.created_at || detail.updated_at}
          <div class="agent-banner-date">{formatDate(detail.updated_at || detail.created_at)}</div>
        {/if}
      </div>
    </div>

    <h1 class="main-title">
      {detail.name || detail.title || 'Untitled assessment'}
      {#if statusBadge}
        <span class="title-score-badge tone-{statusBadge.tone}">{statusBadge.label}</span>
      {/if}
    </h1>
    {#if detail.display_ref}
      <div class="meta-row">
        <span class="ref">REF #{detail.display_ref}</span>
      </div>
    {/if}

    {#if detail.generation_status === 'queued' || detail.generation_status === 'processing'}
      <div class="banner processing">
        <i class="spinner loading icon" aria-hidden="true"></i>
        Generating recommendation… this page will update automatically.
        <button type="button" class="link-btn" on:click={load}>Refresh now</button>
      </div>
    {/if}

    {#if detail.generation_error && detail.generation_status === 'failed'}
      <div class="banner error">{detail.generation_error}</div>
    {/if}

    <div class="tabs-nav" role="tablist">
      <button
        type="button"
        role="tab"
        class="tab-btn"
        class:active={activeTab === 'content'}
        aria-selected={activeTab === 'content'}
        on:click={() => (activeTab = 'content')}
      >
        <i class="file alternate outline icon" aria-hidden="true"></i>
        Assessment
      </button>
      <button
        type="button"
        role="tab"
        class="tab-btn"
        class:active={activeTab === 'answers'}
        aria-selected={activeTab === 'answers'}
        on:click={() => (activeTab = 'answers')}
      >
        <i class="question circle outline icon" aria-hidden="true"></i>
        Your Answers
        {#if answersCount > 0}
          <span class="tab-badge">{answersCount}</span>
        {/if}
      </button>
    </div>

    {#if activeTab === 'content'}
      <div class="content-card">
        {#if result?.verdict_label}
          <div class="verdict-box">
            <p class="verdict-line"><strong>Verdict:</strong> {result.verdict_label}</p>
            {#if result.recommended_approach}
              <p class="approach-line"><strong>Recommended approach:</strong> {result.recommended_approach}</p>
            {/if}
          </div>
        {/if}

        {#if result?.analysis_sections && Array.isArray(result.analysis_sections)}
          {#each result.analysis_sections as sec, i (i)}
            {#if sec?.heading}
              <h2 class="section-h">{sec.heading}</h2>
              <div class="section-body">{sec.body || ''}</div>
            {/if}
          {/each}
        {:else if detail.generation_status === 'completed' && !result}
          <p class="placeholder">No structured result stored.</p>
        {:else if detail.generation_status !== 'completed'}
          <div class="placeholder">
            <h3>Recommendation not yet generated</h3>
            <p>
              Use <strong>Resume</strong> above to open the assessment steps. On the last step, choose
              <strong>Generate Assessment</strong> to run Illuminator. This page will update when the verdict is ready.
            </p>
          </div>
        {/if}
      </div>
    {:else if activeTab === 'answers'}
      <div class="answers-card">
        {#each ANSWER_FIELDS as field, idx}
          <div class="answer-item-full">
            <div class="answer-question-full">
              <span class="q-num">0{idx + 1}</span>
              <div class="q-text">
                <strong>{field.label}</strong>
                <span class="q-prompt">{field.prompt}</span>
              </div>
            </div>
            <div class="answer-text-full">
              {detail[field.key] || '—'}
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </div>

  <ConfirmDialog
    bind:show={showDeleteConfirm}
    type="danger"
    icon="trash"
    title="Delete assessment"
    message={deleteConfirmMessage}
    confirmText="Delete"
    cancelText="Cancel"
    on:confirm={confirmDeleteAssessment}
  />
{/if}

<style>
  .loading {
    padding: 3rem;
    text-align: center;
    color: var(--grey-secondary);
  }

  .view-page {
    margin: 0 auto;
    text-align: left;
    padding-bottom: var(--space-8);
  }

  .top-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1.5rem;
  }

  .back-link {
    color: var(--grey-secondary);
    text-decoration: none;
    font-weight: 400;
  }

  .back-link:hover { color: var(--primary); }

  .top-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .agent-banner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 3px solid var(--accent-teal);
    padding-bottom: 12px;
    margin-bottom: 1.5rem;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .agent-badge {
    display: inline-block;
    background: var(--accent-teal);
    color: var(--white);
    font-weight: 700;
    letter-spacing: 3px;
    padding: 8px 20px;
    border-radius: 8px;
    font-size: 13px;
  }

  .agent-banner-right { text-align: right; }

  .agent-banner-title {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 2px;
    color: var(--grey-secondary);
  }

  .agent-banner-date {
    font-size: 12px;
    color: var(--grey-secondary);
    margin-top: 2px;
  }

  .main-title {
    font-size: 1.75rem;
    font-weight: 500;
    margin: 0 0 0.35rem 0;
    color: var(--grey);
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .title-score-badge {
    font-size: 0.75rem;
    font-weight: 500;
    padding: 0.25rem 0.65rem;
    border-radius: 999px;
    letter-spacing: 0.02em;
  }

  .tone-info    { background: var(--status-info-soft,    #e4f0ff); color: var(--status-info-ink,    #1d4ed8); }
  .tone-success { background: var(--status-success-soft, #e6f7ee); color: var(--status-success-ink, #0e7f47); }
  .tone-danger  { background: var(--status-danger-soft,  #fde8ec); color: var(--status-danger-ink,  #8b1c2d); }
  .tone-neutral { background: var(--greyish,             #eee);    color: var(--grey,               #333);   }

  .meta-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--grey-secondary);
    font-size: 0.9rem;
    margin-bottom: 1.25rem;
  }

  .btn-primary,
  .btn-secondary,
  .btn-accent,
  .btn-danger-outline {
    cursor: pointer;
    border-radius: 6px;
    padding: 10px 16px;
    font-weight: 500;
    font-size: 0.95rem;
  }

  .btn-primary,
  .btn-secondary,
  .btn-accent {
    border: 0;
  }

  .btn-primary { background: var(--primary); color: var(--white); }
  .btn-secondary { background: var(--white); color: var(--grey); border: 1px solid var(--border-color); }
  .btn-accent { background: var(--primary); color: var(--white); }
  .btn-danger-outline {
    background: var(--white);
    color: var(--status-danger-ink, #8b1c2d);
    border: 1px solid var(--status-danger-soft, #f5c2c7);
  }
  .btn-danger-outline:hover:not(:disabled) {
    background: var(--status-danger-soft, #fde8ec);
    border-color: var(--status-danger-ink, #8b1c2d);
  }
  .btn-accent:disabled,
  .btn-primary:disabled,
  .btn-danger-outline:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .banner {
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 1rem;
  }

  .banner.processing {
    background: var(--status-info-soft, #e4f0ff);
    color: var(--status-info-ink, #1d4ed8);
  }

  .banner.error {
    background: var(--status-danger-soft, #fde8ec);
    color: var(--status-danger-ink, #8b1c2d);
  }

  .link-btn {
    margin-left: 0.5rem;
    background: none;
    border: none;
    color: var(--primary);
    cursor: pointer;
    text-decoration: underline;
    font: inherit;
  }

  .tabs-nav {
    display: flex;
    gap: 0.25rem;
    border-bottom: 2px solid var(--border-color);
    margin-bottom: 1.25rem;
  }

  .tab-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: transparent;
    border: 0;
    border-bottom: 2px solid transparent;
    margin-bottom: -2px;
    color: var(--grey-secondary);
    font-size: 0.95rem;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
  }

  .tab-btn:hover { color: var(--grey); }

  .tab-btn.active {
    color: var(--primary);
    border-bottom-color: var(--primary);
  }

  .tab-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.25rem;
    height: 1.25rem;
    padding: 0 0.4rem;
    background: var(--primary);
    color: var(--white);
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .content-card,
  .answers-card {
    background: var(--white);
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid var(--border-color);
  }

  .verdict-box {
    background: var(--status-warn-soft, #fff8e6);
    border: 1px solid var(--accent-yellow-soft, #f0e0b2);
    padding: 1rem 1.25rem;
    border-radius: 8px;
    margin-bottom: 1.5rem;
  }

  .verdict-line, .approach-line { margin: 0.25rem 0; color: var(--grey); }

  .section-h {
    font-size: 1.05rem;
    font-weight: 500;
    margin: 1.25rem 0 0.5rem;
    color: var(--grey);
  }

  .section-body {
    white-space: pre-wrap;
    line-height: 1.55;
    color: var(--grey);
  }

  .placeholder {
    padding: 1rem 0;
    color: var(--grey-secondary);
  }

  .placeholder h3 {
    margin: 0 0 0.5rem 0;
    color: var(--grey);
    font-size: 1.1rem;
    font-weight: 500;
  }

  .answer-item-full {
    padding: 1rem 0;
    border-bottom: 1px solid var(--border-color);
  }
  .answer-item-full:last-child { border-bottom: 0; }

  .answer-question-full {
    display: flex;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
  }

  .q-num {
    flex: 0 0 auto;
    font-size: 1rem;
    font-weight: 500;
    color: var(--grey-secondary);
  }

  .q-text strong {
    display: block;
    color: var(--grey);
    font-size: 1rem;
    font-weight: 500;
  }

  .q-prompt {
    display: block;
    color: var(--grey-secondary);
    font-size: 0.9rem;
    margin-top: 0.15rem;
  }

  .answer-text-full {
    white-space: pre-wrap;
    color: var(--grey);
    line-height: 1.55;
    padding-left: calc(1rem + 0.75rem);
  }

  @media (max-width: 640px) {
    .answer-text-full { padding-left: 0; }
  }
</style>
